import { isNoConfiguredProviderResponse, NoConfiguredProviderError } from "@/lib/edgeFnError";
import { compressImage } from "@/lib/media/extractFrame";
import { extractAudioAsWav } from "@/lib/media/extractAudio";
import { fileSha256 } from "@/lib/audio/sha256";
import type {
  AnalyzeCreativeInput,
  AnalyzeCreativeResult,
  CriativoStage,
  CriativoAnalysis,
} from "../types";

export interface AnalyzeCreativeOptions extends AnalyzeCreativeInput {
  token: string;
  onStageChange?: (stage: CriativoStage) => void;
  signal?: AbortSignal;
}

function getEndpoint(): string {
  const supabaseUrl = (import.meta.env.VITE_SUPABASE_URL as string | undefined) ?? "";
  if (!supabaseUrl) throw new Error("VITE_SUPABASE_URL is not configured");
  return supabaseUrl.replace(/\/$/, "") + "/functions/v1/llm-proxy/analyze-creative";
}

export async function analyzeCreative({
  file,
  veiculacao,
  objetivo,
  label,
  transcriptText,
  token,
  onStageChange,
  signal,
}: AnalyzeCreativeOptions): Promise<AnalyzeCreativeResult> {
  const notify = (s: CriativoStage) => onStageChange?.(s);

  const isVideo = file.type.startsWith("video/") || /\.(mp4|mov|webm|m4v)$/i.test(file.name);
  const isImage = file.type.startsWith("image/") || /\.(jpe?g|png|webp|gif)$/i.test(file.name);
  if (!isVideo && !isImage) {
    throw new Error("Formato não suportado. Envie imagem (JPG/PNG) ou vídeo (MP4/MOV).");
  }

  const MAX = 200 * 1024 * 1024;
  if (file.size > MAX) {
    throw new Error("Arquivo muito grande (limite 200MB).");
  }

  const pasteTranscript = transcriptText?.trim() ?? "";
  const usePasted = pasteTranscript.length > 0;

  notify("preparing");
  const idempotencyKey = await fileSha256(file);

  let imageBlob: Blob | null = null;
  let audioBlob: Blob | null = null;

  if (isVideo) {
    if (!usePasted) {
      notify("extracting-audio");
      try {
        audioBlob = await extractAudioAsWav(file, 16000);
        // If audio is over Whisper's 25MB limit, drop it (analysis still proceeds without).
        if (audioBlob && audioBlob.size > 24 * 1024 * 1024) {
          audioBlob = null;
        }
      } catch {
        audioBlob = null;
      }
    }
  } else {
    imageBlob = await compressImage(file, 1280, 0.9);
  }

  if (isVideo && !usePasted && !audioBlob) {
    throw new Error("Não foi possível extrair o áudio do vídeo. Envie um vídeo com fala ou cole a transcrição.");
  }

  notify(audioBlob ? "transcribing" : "analyzing");

  // JSON+base64 transport: Supabase Edge Runtime corrupts binary multipart
  // parts via UTF-8 re-encoding. Mirrors /analyze-creative-paste.
  const blobToBase64 = async (blob: Blob): Promise<string> => {
    const ab = await blob.arrayBuffer();
    const bytes = new Uint8Array(ab);
    let bin = "";
    const CHUNK = 0x8000;
    for (let i = 0; i < bytes.length; i += CHUNK) {
      bin += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
    }
    return btoa(bin);
  };

  const imageB64 = imageBlob ? await blobToBase64(imageBlob) : undefined;
  const audioB64 = audioBlob ? await blobToBase64(audioBlob) : undefined;

  const body = {
    image_b64: imageB64,
    image_mime: imageBlob?.type || undefined,
    audio_b64: audioB64,
    audio_mime: audioBlob?.type || "audio/wav",
    audio_name: audioBlob ? "audio.wav" : undefined,
    transcript_text: usePasted ? pasteTranscript : undefined,
    media_kind: isVideo ? "video" : "image",
    veiculacao,
    objetivo: objetivo || undefined,
    label: label ?? file.name.replace(/\.[^.]+$/, ""),
    idempotency_key: idempotencyKey,
  };

  notify("analyzing");

  const resp = await fetch(getEndpoint(), {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
    signal,
  });

  if (!resp.ok) {
    const errBody = await resp.json().catch(() => ({ error: resp.statusText }));
    if (isNoConfiguredProviderResponse(resp.status, errBody)) {
      throw new NoConfiguredProviderError(
        typeof errBody.detail === "string" ? errBody.detail : "no_configured_provider",
        { status: resp.status, code: typeof errBody.error === "string" ? errBody.error : undefined },
      );
    }
    if (errBody.error === "duplicate") {
      throw new Error("Esse criativo já foi analisado recentemente.");
    }
    if (errBody.error === "rate_limit_exceeded") {
      throw new Error("Limite de 30 análises por hora atingido. Tente em instantes.");
    }
    throw new Error(
      typeof errBody.detail === "string"
        ? errBody.detail
        : typeof errBody.error === "string"
          ? errBody.error
          : `Erro HTTP ${resp.status}`,
    );
  }

  const data = (await resp.json()) as {
    job_id: string;
    criativo_id: string;
    transcript: string;
    analysis: CriativoAnalysis;
  };

  notify("done");

  return {
    jobId: data.job_id,
    criativoId: data.criativo_id,
    transcript: data.transcript ?? "",
    analysis: data.analysis ?? {},
  };
}
